"use client";

import { Suspense } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { parseAsString } from "nuqs";
import { type IQuerySearchParam } from "@/types/query-search-params";
import { useQuerySearchParams } from "@/hooks/use-query-search-params";
import { useMutation } from "@tanstack/react-query";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from "@/components/ui/form";
import {
  SkeletonBox,
  SkeletonContainer,
  SkeletonLabel,
} from "@/components/ui/skeleton";
import { AuthPageHeader, AuthPageHeaderSkeleton } from "../page-header";
import { SubmitButton } from "@/components/ui/button";
import { OtpInput } from "../otp-input";
import { useVerifyEmail } from "../../hooks/use-verify-email";
import { resendVerification } from "@/lib/api/generated";
import { showToast } from "@/components/ui/sonner";
import { useCooldown } from "@/hooks/use-cooldown";
import { Icon } from "@/components/ui/icon";

const queryParams = {
  EMAIL: {
    field: "email",
    parser: parseAsString.withDefault(""),
  },
  PAT: {
    field: "pat",
    parser: parseAsString.withDefault(""),
  },
  ORGANIZATION_ID: {
    field: "organization_id",
    parser: parseAsString.withDefault(""),
  },
} satisfies Record<string, IQuerySearchParam>;

const verifySchema = z.object({
  code: z.string().length(6, "Enter the 6-digit code"),
});

type VerifyValues = z.infer<typeof verifySchema>;

function VerifyContent() {
  const [email] = useQuerySearchParams(queryParams.EMAIL);
  const [patParam] = useQuerySearchParams(queryParams.PAT);
  const [organizationIdParam] = useQuerySearchParams(queryParams.ORGANIZATION_ID);
  const pat = patParam !== "" ? patParam : undefined;
  const organizationId = organizationIdParam !== "" ? organizationIdParam : undefined;
  const verify = useVerifyEmail();
  const { cooldown, start: startCooldown, isActive: cooldownActive } = useCooldown(60);

  const form = useForm<VerifyValues>({
    resolver: zodResolver(verifySchema),
    mode: "onSubmit",
    defaultValues: { code: "" },
  });

  const resendMutation = useMutation({
    mutationFn: () => resendVerification({ email }),
    onSuccess: () => {
      showToast.success("Code resent", { description: "A new verification code has been sent." });
      startCooldown();
    },
    onError: (error) => {
      const message =
        error instanceof Error && error.message
          ? error.message
          : "Failed to resend verification code.";
      showToast.error("Resend failed", { description: message });
    },
  });

  function handleSubmit(values: VerifyValues) {
    if (!email) return;
    verify.mutate(
      {
        email,
        code: values.code,
        pending_authentication_token: pat,
        organization_id: organizationId,
      },
      {
        onError: () =>
          form.setError("code", {
            message: "The code is incorrect, please verify and try again.",
          }),
      }
    );
  }

  function handleResend() {
    if (!email || cooldownActive) return;
    resendMutation.mutate();
  }

  return (
    <>
      <AuthPageHeader
        title="Account Verification"
        subtitle={<>We&apos;ve sent a code to{" "}<span className="font-medium text-foreground">{email || "your email"}</span>. Please enter the code below to verify your account.</>}
      />

      <Form {...form}>
        <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-6">
          <FormField
            control={form.control}
            name="code"
            render={({ field, fieldState }) => (
              <FormItem className="flex flex-col gap-3">
                <div className="flex items-center justify-between text-sm leading-5">
                  <span className="font-medium text-foreground">Verification Code</span>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={cooldownActive || resendMutation.isPending}
                    className="text-foreground/70 hover:text-foreground transition-colors disabled:opacity-50"
                  >
                    {resendMutation.isPending ? "Sending..." : cooldown > 0 ? `Resend in ${cooldown}s` : "Resend OTP"}
                  </button>
                </div>

                <FormControl>
                  <OtpInput
                    length={6}
                    disabled={verify.isPending}
                    error={!!fieldState.error}
                    onComplete={(code) => {
                      field.onChange(code);
                      form.handleSubmit(handleSubmit)();
                    }}
                  />
                </FormControl>

                <FormMessage />
              </FormItem>
            )}
          />

          <SubmitButton type="submit" variant="linear-ai" size="auth" loading={verify.isPending}>
            Verify Account <Icon name="IconArrowRight" size={16} />
          </SubmitButton>
        </form>
      </Form>
    </>
  );
}

function VerifyEmailSkeleton() {
  return (
    <>
      <AuthPageHeaderSkeleton />
      <SkeletonContainer className="flex-col gap-6">
        <div className="flex flex-col gap-3">
          <div className="flex items-center justify-between">
            <SkeletonLabel className="w-32" />
            <SkeletonLabel tone="muted" className="w-20" />
          </div>
          <div className="flex items-start justify-between w-full gap-2">
            {Array.from({ length: 6 }, (_, i) => (
              <SkeletonBox key={i} className="size-[58px] rounded-md" />
            ))}
          </div>
        </div>
        <SkeletonBox className="h-10 w-full rounded-md" />
      </SkeletonContainer>
    </>
  );
}

export function VerifyEmail() {
  return (
    <Suspense fallback={<VerifyEmailSkeleton />}>
      <VerifyContent />
    </Suspense>
  );
}
